
  // Owns popup expansion and active-descendant navigation. Selection and the
  // search itself stay on the server: this only moves the active option and
  // clicks the one the user commits to, so the caller's event handlers remain
  // the single source of truth for the value.
  export default {
    mounted() {
      this.input = this.el.querySelector("[data-combobox-input]")
      this.listbox = this.el.querySelector("[data-combobox-listbox]")
      this.activeIndex = -1
      this.selectedValue = this.el.querySelector("[data-combobox-value]").value

      this.onKeyDown = (event) => {
        const options = this.options()
        switch (event.key) {
          case "ArrowDown":
            event.preventDefault()
            if (!this.fresh()) break
            this.open()
            this.moveActive(options.length ? (this.activeIndex + 1) % options.length : -1)
            break
          case "ArrowUp":
            event.preventDefault()
            if (!this.fresh()) break
            this.open()
            this.moveActive(
              options.length ? (this.activeIndex - 1 + options.length) % options.length : -1
            )
            break
          case "Enter":
            if (this.expanded() && options[this.activeIndex]) {
              event.preventDefault()
              options[this.activeIndex].click()
              this.close()
            }
            break
          case "Escape":
            if (this.expanded()) {
              event.preventDefault()
              this.close()
              this.input.focus()
            }
            break
          case "Tab":
            this.close()
            break
        }
      }

      // phx-keyup is debounced, so between a keystroke and the server's
      // reply the rendered options still answer the PREVIOUS query. Opening
      // over them lets Enter or a click commit a place the user has already
      // typed past. The listbox carries the query its options answer;
      // anything else is stale and stays shut until fresh results arrive.
      this.onInput = () => {
        this.selectionCommitted = false
        this.fresh() ? this.open() : this.close()
      }
      this.onFocus = () => {
        this.selectionCommitted = false
        if (this.options().length && this.fresh()) this.open()
      }
      this.onDocClick = (event) => { if (!this.el.contains(event.target)) this.close() }

      // Committing to one of our own controls is not "leaving without
      // selecting". mousedown moves focus before click runs, so the
      // caller's phx-blur would fire first -- and callers resolve the typed
      // text on blur, which for Add Location means a Google round trip plus
      // a place upsert for text the user is in the act of replacing. It
      // costs latency and can transiently commit a different place than the
      // one clicked. Preventing mousedown's default keeps focus on the
      // input so no blur is emitted; the click still fires normally.
      this.onMouseDown = (event) => {
        if (event.target.closest("[data-combobox-option], [data-combobox-clear]")) {
          event.preventDefault()
        }
      }

      this.onClick = (event) => {
        const option = event.target.closest("[data-combobox-option]")
        if (option) {
          this.selectionCommitted = true
          this.input.value = option.dataset.comboboxLabel
          this.close()
        }
      }

      this.input.addEventListener("keydown", this.onKeyDown)
      this.input.addEventListener("input", this.onInput)
      this.input.addEventListener("focus", this.onFocus)
      this.el.addEventListener("mousedown", this.onMouseDown)
      this.el.addEventListener("click", this.onClick)
      document.addEventListener("click", this.onDocClick)
    },

    updated() {
      // Fresh results arrived: reopen if the user is still typing, and drop
      // the active option since the list it pointed into no longer exists.
      this.input = this.el.querySelector("[data-combobox-input]")
      this.listbox = this.el.querySelector("[data-combobox-listbox]")
      this.activeIndex = -1
      const selectedValue = this.el.querySelector("[data-combobox-value]").value
      if (selectedValue !== this.selectedValue) {
        // LiveView preserves the focused input's value during patches.
        // A committed selection must replace that query explicitly.
        this.input.value = this.el.dataset.selectedLabel || ""
        this.selectedValue = selectedValue
        this.close()
        return
      }
      if (this.selectionCommitted) {
        this.close()
        return
      }
      if (document.activeElement === this.input && this.options().length && this.fresh()) {
        this.open()
      } else if (!this.fresh()) {
        this.close()
      }
    },

    destroyed() {
      document.removeEventListener("click", this.onDocClick)
    },

    options() {
      return Array.from(this.el.querySelectorAll("[data-combobox-option]"))
    },
    // True when the rendered options answer the text currently in the input.
    fresh() {
      return (this.listbox.dataset.comboboxOptionsFor ?? "") === this.input.value
    },
    expanded() {
      return this.input.getAttribute("aria-expanded") === "true"
    },
    open() {
      this.listbox.hidden = false
      this.input.setAttribute("aria-expanded", "true")
    },
    close() {
      this.listbox.hidden = true
      this.input.setAttribute("aria-expanded", "false")
      this.input.removeAttribute("aria-activedescendant")
      this.options().forEach((o) => o.removeAttribute("data-active"))
      this.activeIndex = -1
    },
    moveActive(index) {
      const options = this.options()
      options.forEach((o) => o.removeAttribute("data-active"))
      this.activeIndex = index
      const active = options[index]
      if (!active) {
        this.input.removeAttribute("aria-activedescendant")
        return
      }
      active.setAttribute("data-active", "true")
      this.input.setAttribute("aria-activedescendant", active.id)
      active.scrollIntoView({ block: "nearest" })
    },
  }
